import axios from 'axios';

const api = axios.create({
  baseURL: process.env.NODE_ENV === 'production' 
    ? process.env.REACT_APP_API_URL
    : 'http://localhost:5000/api',
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json, text/plain, */*'
  },
  withCredentials: true,
  timeout: 10000
});

// Add request interceptor to add auth token and handle FormData
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // If sending FormData, remove Content-Type header to let browser set it with boundary
    // but preserve other custom headers
    if (config.data instanceof FormData) {
      // Store custom headers before deleting Content-Type
      const customHeaders = { ...config.headers };
      delete customHeaders['Content-Type'];
      config.headers = customHeaders;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Combined response interceptor for error handling and auth
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized errors
    if (error.response?.status === 401) {
      // Check if this is a guest session
      const guestSessionId = localStorage.getItem('guestSessionId') || sessionStorage.getItem('guestSessionId');
      const isGuestSession = !!guestSessionId;
      
      // Only clear auth data and redirect for non-guest sessions
      if (!isGuestSession) {
        // Clear auth data
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        
        // Dispatch storage event
        window.dispatchEvent(new Event('storage'));
        
        // Only redirect if not already on login page
        if (!window.location.pathname.includes('/login')) {
          window.location.href = '/login';
        }
      }
    }
    
    // Enhanced error handling for other status codes
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      return Promise.reject({
        ...error,
        message: error.response.data.message || 'An error occurred'
      });
    } else if (error.request) {
      // The request was made but no response was received
      return Promise.reject({
        ...error,
        message: 'No response from server'
      });
    } else {
      // Something happened in setting up the request that triggered an Error
      return Promise.reject({
        ...error,
        message: 'Error setting up request'
      });
    }
  }
);

// Auth services
export const authService = {
  register: (userData) => api.post('/auth/register', userData),
  verify: (data) => api.post('/auth/verify', data),
  resendVerification: (email) => api.post('/auth/resend-verification', { email }),
  login: (credentials) => api.post('/auth/login', credentials),
  completeTwoFactorLogin: (data) => api.post('/auth/complete-2fa-login', data),
  setupPassword: (data) => api.post('/auth/setup-password', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  changePassword: (data) => api.post('/auth/change-password', data),
  getLoginHistory: (data) => api.post('/auth/login-history', data),
  
  // Two-Factor Authentication
  getTwoFactorStatus: () => api.get('/auth/2fa/status'),
  generateTOTPSecret: () => api.post('/auth/2fa/totp/generate'),
  sendEmailTwoFactorCode: () => api.post('/auth/2fa/email/send'),
  verifyTwoFactorSetup: (method, code, data) => api.post('/auth/2fa/verify-setup', { method, code, data }),
  disableTwoFactor: (method) => api.post('/auth/2fa/disable', { method }),
  verifyTwoFactorLogin: (method, code) => api.post('/auth/2fa/verify-login', { method, code }),
};

// User services
export const userService = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', data),
  changePassword: (data) => api.post('/users/change-password', data),
  getUserStats: () => api.get('/proposal-comparisons/user-stats'),
};

// Preferences services
export const preferencesService = {
  // Save or update user preferences
  savePreferences: (data) => api.post('/preferences/save', data),
  
  // Get user preferences
  getPreferences: () => api.get('/preferences'),
  
  // Check if user has completed preferences
  checkStatus: () => api.get('/preferences/status'),
  
  // Delete user preferences
  deletePreferences: () => api.delete('/preferences')
};

// Stream document chat message
export const streamDocQuery = async (query, documents, onToken, onComplete, onError) => {
  // Create an AbortController to manage the fetch request
  const controller = new AbortController();
  let eventSource = null;
  let timeoutId = null;
  
  // Function to clean up resources
  const cleanup = () => {
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  try {
    // Get the authentication token
    const token = localStorage.getItem('token');
    
    // First, initiate the streaming session
    const response = await fetch(`${api.defaults.baseURL}/doc-chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
      },
      body: JSON.stringify({ query, documents, stream: true }),
      credentials: 'include',
      signal: controller.signal
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to initiate streaming session');
    }

    const data = await response.json();
    const { sessionId, relevantDocs } = data;

    if (!sessionId) {
      throw new Error('No session ID returned from server');
    }

    // Now connect to the streaming endpoint
    const url = new URL(`${api.defaults.baseURL}/doc-chat/stream`);
    url.searchParams.append('sessionId', sessionId);
    
    // For EventSource, we need to add the token as a query parameter
    // since we can't set headers directly
    if (token) {
      url.searchParams.append('token', token);
    }
    
    eventSource = new EventSource(url.toString(), { withCredentials: true });
    
    // Set a timeout to close the connection if no activity for 60 seconds
    const resetTimeout = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log('Stream timeout - no activity for 60 seconds');
        cleanup();
        onComplete();
      }, 60000); // 60 seconds
    };
    
    resetTimeout();

    eventSource.onmessage = (event) => {
      resetTimeout();
      
      try {
        const eventData = JSON.parse(event.data);
        
        // Handle different event types
        if (eventData.connected) {
          console.log('Stream connected');
        } else if (eventData.token) {
          onToken(eventData.token);
        } else if (eventData.complete) {
          cleanup();
          onComplete();
        } else if (eventData.error) {
          cleanup();
          onError(new Error(eventData.error));
        } else if (eventData.ping) {
          console.log('Received ping from server');
        }
      } catch (err) {
        console.error('Error parsing event data:', err);
        cleanup();
        onError(err);
      }
    };

    eventSource.onerror = (err) => {
      console.error('EventSource error:', err);
      cleanup();
      onError(new Error('Stream connection error'));
    };

    // Return relevant docs and a cancel function
    return {
      relevantDocs,
      cancel: () => {
        console.log('Canceling doc chat stream');
        cleanup();
        controller.abort();
      }
    };
  } catch (error) {
    console.error('Error setting up doc chat stream:', error);
    cleanup();
    onError(error);
    return { cancel: () => {} };
  }
};

export const streamMessage = async (message, onToken, onComplete, onError) => {
  // Create an AbortController to manage the fetch request
  const controller = new AbortController();
  let eventSource = null;
  let timeoutId = null;
  
  // Function to clean up resources
  const cleanup = () => {
    if (eventSource) {
      eventSource.close();
      eventSource = null;
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  try {
    // Get the authentication token
    const token = localStorage.getItem('token');
    
    // First, initiate the streaming session
    const response = await fetch(`${api.defaults.baseURL}/chat`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token}` : ''
      },
      body: JSON.stringify({ message, stream: true }),
      credentials: 'include',
      signal: controller.signal
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to initiate streaming session');
    }

    const data = await response.json();
    const { sessionId } = data;

    if (!sessionId) {
      throw new Error('No session ID returned from server');
    }

    // Now connect to the streaming endpoint
    const url = new URL(`${api.defaults.baseURL}/chat/stream`);
    url.searchParams.append('sessionId', sessionId);
    
    // For EventSource, we need to add the token as a query parameter
    // since we can't set headers directly
    if (token) {
      url.searchParams.append('token', token);
    }
    
    eventSource = new EventSource(url.toString(), { withCredentials: true });
    
    // Set a timeout to close the connection if no activity for 60 seconds
    const resetTimeout = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log('Stream timeout - no activity for 60 seconds');
        cleanup();
        onComplete();
      }, 60000); // 60 seconds
    };
    
    resetTimeout();

    eventSource.onmessage = (event) => {
      resetTimeout();
      
      try {
        const eventData = JSON.parse(event.data);
        
        // Handle different event types
        if (eventData.connected) {
          console.log('Stream connected');
        } else if (eventData.token) {
          onToken(eventData.token);
        } else if (eventData.complete) {
          cleanup();
          onComplete();
        } else if (eventData.error) {
          cleanup();
          onError(new Error(eventData.error));
        } else if (eventData.ping) {
          console.log('Received ping from server');
        }
      } catch (err) {
        console.error('Error parsing event data:', err);
        cleanup();
        onError(err);
      }
    };

    eventSource.onerror = (err) => {
      console.error('EventSource error:', err);
      cleanup();
      onError(new Error('Stream connection error'));
    };

    // Return a function to abort the stream if needed
    return () => {
      console.log('Aborting stream');
      controller.abort();
      cleanup();
    };
  } catch (error) {
    console.error('Stream setup error:', error);
    cleanup();
    onError(error);
    return () => {}; // Return empty function as there's nothing to abort
  }
};

export const chatService = {
  sendMessage: async (message) => {
    try {
      const response = await api.post('/chat', { message });
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },
  getHistory: async () => {
    try {
      const response = await api.get('/chat/history');
      return response.data;
    } catch (error) {
      console.error('Error getting chat history:', error);
      throw error;
    }
  }
};

export const getFileDownloadUrl = (projectId, fileType, fileName) => {
  // Use the full URL including the backend server address
  return `${api.defaults.baseURL.replace('/api', '')}/uploads/projects/${projectId}/${fileType}/${fileName}`;
  // or if you're not using environment variables:
  // return `http://localhost:5000/uploads/projects/${projectId}/${fileType}/${fileName}`;
};

export const deleteProjectFile = async (projectId, fileId) => {
  try {
      const response = await api.delete(`/data-upload/${projectId}/files/${fileId}`);
      return response;
  } catch (error) {
      if (error.response?.status === 404) {
          throw new Error('File not found');
      }
      throw new Error(error.response?.data?.message || 'Failed to delete file');
  }
};

// Add billing services
export const billingService = {
  // Payment Methods
  getPaymentMethods: () => api.get('/billing/payment-methods'),
  addPaymentMethod: (data) => api.post('/billing/payment-methods', data),
  updatePaymentMethod: (id, data) => api.put(`/billing/payment-methods/${id}`, data),
  deletePaymentMethod: (id) => api.delete(`/billing/payment-methods/${id}`),
  setDefaultPaymentMethod: (id) => api.put(`/billing/payment-methods/${id}/set-default`),

  // Billing Addresses
  getBillingAddresses: () => api.get('/billing/billing-addresses'),
  createBillingAddress: (data) => api.post('/billing/billing-addresses', data),
  updateBillingAddress: (id, data) => api.put(`/billing/billing-addresses/${id}`, data),
  deleteBillingAddress: (id) => api.delete(`/billing/billing-addresses/${id}`),
  setDefaultBillingAddress: (id) => api.put(`/billing/billing-addresses/${id}/set-default`),

  // Plans and Subscriptions
  getCurrentPlan: () => api.get('/billing/current-plan'),
  getBillingPlans: () => api.get('/billing/plans'),
  updateSubscription: async (subscriptionData) => {
    try {
      const response = await api.post('/billing/subscriptions', subscriptionData);
      return response;
    } catch (error) {
      console.error('Error updating subscription:', error);
      throw error;
    }
  },
  cancelSubscription: () => api.post('/billing/subscriptions/cancel'),
  resumeSubscription: () => api.post('/billing/subscriptions/resume'),

  // Billing History
  getBillingHistory: async () => {
    try {
      const response = await api.get('/billing/history');
      if (response.data.status === 'success') {
        // Ensure amounts are numbers
        response.data.data = response.data.data.map(record => ({
          ...record,
          amount: parseFloat(record.amount)
        }));
      }
      return response;
    } catch (error) {
      console.error('Error fetching billing history:', error);
      throw error;
    }
  },
  createBillingRecord: (data) => api.post('/billing/history', data),
  downloadInvoice: (invoiceId) => api.get(`/billing/invoices/${invoiceId}/download`, {
    responseType: 'blob'
  }),
  downloadAllInvoices: () => api.get('/billing/invoices/download-all', {
    responseType: 'blob'
  })
};

// LLM provider services
export const llmProviderService = {
  toggleProvider: (useOpenAI) => api.post('/chat/llm-provider', { useOpenAI }),
  getProvider: () => api.get('/chat/llm-provider'),
};

// Proposal comparison services (updated to support guest sessions)
export const proposalComparisonService = {
  // Guest session helper to add headers
  _addGuestHeaders: (config = {}) => {
    const guestSessionId = localStorage.getItem('guestSessionId') || sessionStorage.getItem('guestSessionId');
    if (guestSessionId) {
      return {
        ...config,
        headers: {
          ...config.headers,
          'x-guest-session-id': guestSessionId
        }
      };
    }
    return config;
  },

  // Create comparison (supports both authenticated users and guests)
  createComparison: (data) => {
    // For guests, use the guest endpoint
    if (data.isGuestUser && data.guestSessionId) {
      const config = proposalComparisonService._addGuestHeaders();
      return api.post('/proposal-comparisons/guest', data, config);
    }
    // For authenticated users, use the regular endpoint
    return api.post('/proposal-comparisons', data);
  },

  // Get comparison by ID (supports guest access)
  getComparisonById: (id, isGuest = false) => {
    const config = proposalComparisonService._addGuestHeaders();
    const url = isGuest ? 
      `/proposal-comparisons/${id}?guest=true` : 
      `/proposal-comparisons/${id}`;
    return api.get(url, config);
  },

  // Upload contractor files (supports guest access)
  uploadContractorFiles: (comparisonId, contractor, formData) => {
    const guestSessionId = localStorage.getItem('guestSessionId') || sessionStorage.getItem('guestSessionId');
    const token = localStorage.getItem('token');
    const isAuthenticated = !!token;
    const isGuestUser = !!guestSessionId && !token;
    
    const config = {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    };
    
    // Only add guest session header for actual guest users (not authenticated users)
    if (isGuestUser) {
      config.headers['x-guest-session-id'] = guestSessionId;
    }
    
    return api.post(`/proposal-comparisons/${comparisonId}/upload/${contractor}`, formData, config);
  },

  // Regular authenticated user methods
  getUserComparisons: () => api.get('/proposal-comparisons'),
  updateComparison: (id, data) => api.put(`/proposal-comparisons/${id}`, data),
  deleteComparison: (id) => api.delete(`/proposal-comparisons/${id}`),
  getComparisonFiles: (comparisonId) => api.get(`/proposal-comparisons/${comparisonId}/files`),
  getContractorFiles: (comparisonId, contractor) => 
    api.get(`/proposal-comparisons/${comparisonId}/files/${contractor}`),
  deleteFile: (fileId) => api.delete(`/proposal-comparisons/files/${fileId}`),
  getProjectComparisonData: (userId, projectName) =>
    api.get(`/proposal-comparisons/project-data/${userId}/${projectName}`),

  // Guest session management methods
  migrateGuestToUser: (guestId, userId) => 
    api.post(`/proposal-comparisons/guest/${guestId}/migrate/${userId}`),
  migrateSpecificComparisons: (comparisonIds, userId) =>
    api.post(`/proposal-comparisons/migrate-comparisons/${userId}`, { comparisonIds }),
  getGuestStats: () => 
    api.get('/proposal-comparisons/admin/guest-stats'),
  cleanupExpiredGuests: (daysOld = 30) => 
    api.post('/proposal-comparisons/admin/cleanup-guests', { daysOld }),

  // Utility method to check if user is guest
  isGuestUser: () => {
    const guestSessionId = localStorage.getItem('guestSessionId') || sessionStorage.getItem('guestSessionId');
    const token = localStorage.getItem('token');
    return !!guestSessionId && !token;
  },

  // Method to get current guest session ID
  getGuestSessionId: () => {
    return localStorage.getItem('guestSessionId') || sessionStorage.getItem('guestSessionId');
  },

  // NEW: Start AI processing for proposal comparison (waits for completion)
  startAIProcessing: (comparisonId) => {
    const config = proposalComparisonService._addGuestHeaders({
      timeout: 15 * 60 * 1000 // 15 minutes timeout for AI processing
    });
    return api.post(`/proposal-comparisons/${comparisonId}/process`, {}, config);
  }
};

// Add homeowner billing services
export const homeownerBillingService = {
  // Payment Methods
  getPaymentMethods: () => api.get('/homeowner-billing/payment-methods'),
  createSetupIntent: () => api.post('/homeowner-billing/payment-methods/setup-intent'),
  addPaymentMethod: (data) => api.post('/homeowner-billing/payment-methods', data),
  updatePaymentMethod: (id, data) => api.put(`/homeowner-billing/payment-methods/${id}`, data),
  deletePaymentMethod: (id) => api.delete(`/homeowner-billing/payment-methods/${id}`),

  // Billing Addresses
  getBillingAddresses: () => api.get('/homeowner-billing/billing-addresses'),
  createBillingAddress: (data) => api.post('/homeowner-billing/billing-addresses', data),
  updateBillingAddress: (id, data) => api.put(`/homeowner-billing/billing-addresses/${id}`, data),
  deleteBillingAddress: (id) => api.delete(`/homeowner-billing/billing-addresses/${id}`),

  // Plans and Subscriptions
  getPlans: () => api.get('/homeowner-billing/plans'),
  getSubscription: () => api.get('/homeowner-billing/subscription'),
  updateSubscription: (subscriptionData) => api.post('/homeowner-billing/subscription', subscriptionData),
  cancelSubscription: () => api.post('/homeowner-billing/subscription/cancel'),
  resumeSubscription: () => api.post('/homeowner-billing/subscription/resume'),
  updateSubscriptionPaymentMethod: (subscriptionId, paymentMethodId) => 
    api.put(`/homeowner-billing/subscription/${subscriptionId}/payment-method`, { paymentMethodId }),

  // Billing History
  getBillingHistory: () => api.get('/homeowner-billing/billing-history'),
  downloadInvoice: (invoiceId) => api.get(`/homeowner-billing/invoices/${invoiceId}/download`, {
    responseType: 'blob'
  }),
  downloadAllInvoices: () => api.get('/homeowner-billing/invoices/download-all', {
    responseType: 'blob'
  })
};

// Session management services (Google-like device management)
export const sessionService = {
  // Get all active sessions for the current user
  getActiveSessions: () => api.get('/sessions/active'),
  
  // Get session statistics
  getSessionStats: () => api.get('/sessions/stats'),
  
  // Revoke a specific session
  revokeSession: (sessionId) => api.delete(`/sessions/${sessionId}`),
  
  // Revoke all other sessions except current
  revokeAllOtherSessions: () => api.delete('/sessions/revoke-all'),
  
  // Logout from current session
  logout: () => api.post('/sessions/logout'),
  
  // Admin: Clean up expired sessions
  cleanupExpiredSessions: () => api.delete('/sessions/cleanup')
};

export default api; 